import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "../../axiosInstance";
import * as Yup from "yup";
import Grid from "apollo-react/components/Grid";
import { Formik, Form, Field } from "formik";
import Button from "apollo-react/components/Button";
import { renderInputComponent } from "../common/Form/renderInputComponent";
import "./style.css";
import AuthLayout from "./AuthLayout";

const Register = () => {
	// const [categories, setCategories] = useState([]);

	const [formFields, setFormFields] = useState([
		{
			name: "first_name",
			type: "text",
			label: "First Name",
			required: true,
			fullWidth: false,
		},
		{
			name: "last_name",
			type: "text",
			label: "Last Name",
			required: true,
			fullWidth: false,
		},
		{
			name: "email",
			type: "email",
			label: "Email Address",
			required: true,
			fullWidth: true,
		},
		{
			name: "category",
			type: "dropdown",
			label: "Category",
			options: [],
			required: true,
			fullWidth: true,
		},
		{
			name: "password",
			type: "password",
			label: "Password",
			required: false,
			fullWidth: true,
		},
		{
			name: "confirm_password",
			type: "password",
			label: "Confirm Password",
			required: false,
			fullWidth: true,
		},
		{
			name: "tnc",
			type: "checkbox",
			label: "Terms and Conditions",
			required: false,
			fullWidth: true,
		},
	]);

	const [formData, setFormData] = useState({
		first_name: "",
		last_name: "",
		email: "",
		password: "",
		confirm_password: "",
		category: "",
		tnc: false,
	});
	const [t] = useTranslation();
    // const [resp, setResp] = useState("");
	let navigate = useNavigate();

	const validationSchema = Yup.object().shape({
		first_name: Yup.string()
			.required("First Name is required")
			.min(3, "First Name must be at least 3 characters")
			.max(20, "First Name must not exceed 20 characters"),
		last_name: Yup.string()
			.required("Last Name is required")
			.min(3, "Last Name must be at least 3 characters")
			.max(20, "Last Name must not exceed 20 characters"),
		email: Yup.string()
			.required("Email Address is required")
			.matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format"),
		password: Yup.string()
			.required("Password is required")
			.min(8, "Password must be at least 8 characters")
			.matches(
				/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
				"Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
			),
		confirm_password: Yup.string()
			.required("Confirm Password is required")
			.oneOf([Yup.ref("password"), null], "Passwords must match"),
		category: Yup.string().required("Category is required"),
		tnc: Yup.boolean().oneOf(
			[true],
			"You must accept the Terms and Conditions"
		),
	});

	// Handle input changes
	const handleChange = (name, value) => {
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};
	const handleSubmit = async (e) => {
		try {
			const endpoint = formData.category === "Principal Investigator (PI)" 
				? "/api/users/" 
				: "/api/endUsers/";
				
				const requestData = { ...formData };
				if (requestData.category === "Principal Investigator (PI)") {
					requestData.username = requestData.first_name || requestData.last_name;
					requestData.username = `${requestData.first_name}-${requestData.last_name}-${Date.now()}`;
					requestData.password = requestData.confirm_password;
				}
			axios
				.post(endpoint, requestData)
				.then((resp) => {
					toast.success(t("Registered Successfully!"), {
						position: "top-center",
					});
					navigate("/login");
				})
				.catch((error) => {
					if (error.response && error.response.status === 400) {
						// Handle the specific error for duplicate email
						toast.error(error.response.data.message, {
							position: "top-center",
						});
					} else {
                        // setResp(t("Error creating form"), ":", error);
					}
				});
		} catch (err) {
            // setResp(t("Error submitting form"), ":", err);
			console.error("Error submitting form: ", err);
		}
	};

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const response = await axios.get("/api/categories/public");
				setFormFields((prevFields) =>
					prevFields.map((field) =>
						field.name === "category"
							? {
								...field,
								options: [
									{ key: "Principal Investigator (PI)", label: "Principal Investigator (PI)", value: "Principal Investigator (PI)" },
									...response.data
										.map((category) => ({
											key: category.id,
											label: category.name,
											value: category.id,
										})),
								],
							}
						: field
					)
				);
			} catch (err) {
				console.error("Error fetching public categories:", err);
			}
		};

		fetchCategories();
	}, []);

	return (
		<div className="page-container">
			<AuthLayout>
				{/* Mobile Content - Only visible when left image is hidden */}
				<div className="mobile-content">
					<div className="mobile-hero">
						<h1 className="mobile-title">Drug Registration System (D.R.S)</h1>
						<p className="mobile-description">
							IQVIA's Drug Registration System (DRS) is a centralized, cloud-based platform designed to streamline the entire drug registration lifecycle—from submission to approval—while ensuring regulatory compliance and operational efficiency.
						</p>
					</div>
				</div>
				
				<Grid container justifyContent="center" className="signInTitleGrid">
					<div>
						<h2 className="signInTitle">Register my account</h2>
						<p className="signInDesc">We're glad you're here.</p>
					</div>
				</Grid>
			<Grid container className="formContainer">
				<Grid container justifyContent="center">
					<Grid item xs={12}>
						<Formik
							initialValues={formData}
							enableReinitialize={true}
							validationSchema={validationSchema}
							onSubmit={(values) => {
								// if (!values.tnc) {
								//   toast.error("You must accept the Terms and Conditions", {
								//     position: "top-center",
								//   });
								//   return;
								//
								handleSubmit(values);
							}}
						>
							{({ handleSubmit, setFieldValue }) => (
								<Form onSubmit={handleSubmit}>
									<Grid container spacing={2}>
										<Grid item xs={6}>
											<Field name="first_name">
												{({ field: formikField, meta }) => {
													const handleFieldChange = (value) => {
														setFieldValue(formikField.name, value);
														handleChange(formikField.name, value);
													};

													return renderInputComponent(
														{
															name: "first_name",
															type: "text",
															label: "First Name",
															required: true,
														},
														formData,
														formikField,
														meta,
														handleFieldChange
													);
												}}
											</Field>
										</Grid>
										<Grid item xs={6}>
											<Field name="last_name">
												{({ field: formikField, meta }) => {
													const handleFieldChange = (value) => {
														setFieldValue(formikField.name, value);
														handleChange(formikField.name, value);
													};

													return renderInputComponent(
														{
															name: "last_name",
															type: "text",
															label: "Last Name",
															required: true,
														},
														formData,
														formikField,
														meta,
														handleFieldChange
													);
												}}
											</Field>
										</Grid>
									</Grid>

									{formFields
										.filter(
											(field) =>
												field.name !== "first_name" &&
												field.name !== "last_name"
										)
										.map((field) => (
											<Field key={field.name} name={field.name}>
												{({ field: formikField, meta }) => {
													const handleFieldChange = (value) => {
														setFieldValue(formikField.name, value);
														handleChange(formikField.name, value);
													};

													return renderInputComponent(
														field,
														formData,
														formikField,
														meta,
														handleFieldChange
													);
												}}
											</Field>
										))}

									<Button
										variant="primary"
										type="submit"
										style={{
											width: "100%",
											padding: "10px",
											border: "none",
											margin: "20px 0",
											cursor: "pointer",
										}}
									>
										Register
									</Button>
								</Form>
							)}
						</Formik>
					</Grid>
				</Grid>
			</Grid>
			<Grid container justifyContent="center">
				<p className="signUpText">
					Already have an account? {''}
					<a href="/login" className="signUpLink">
						Sign In
					</a>
				</p>
			</Grid>
		</AuthLayout>
		</div>
	);
};

export default Register;
